import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Checkbox,
	FormControlLabel,
} from "@mui/material";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";

const checkoutSchema = yup.object().shape({
	checkoutEmpID: yup.string().nullable(),
	checkoutDivision: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.nullable(),
	checkoutMileage: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	checkoutNotes: yup
		.string()
		.trim()
		.max(500, maxLengthMessage(500))
		.nullable(),
	performInspection: yup.boolean(),
});

const buildDefaults = () => ({
	checkoutEmpID: "",
	checkoutDivision: "",
	checkoutMileage: "",
	checkoutNotes: "",
	performInspection: false,
});

const CheckInOutVehicleDialog = ({
	open,
	onClose,
	onSave,
	vehicle,
	checkoutMode = "out", // "in" or "out"
	employees = [],
	divisionOptions = [],
	canPerformInspection = false,
}) => {
	const isCheckIn = checkoutMode === "in";

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(checkoutSchema),
		defaultValues: buildDefaults(),
	});

	// Reset form when dialog opens
	useEffect(() => {
		if (open) {
			reset(buildDefaults());
		}
	}, [open, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			checkoutEmpID: data.checkoutEmpID || null,
			checkoutDivision: data.checkoutDivision || null,
			checkoutMileage: data.checkoutMileage || null,
			checkoutNotes: data.checkoutNotes?.trim() || null,
			performInspection: data.performInspection || false,
			isCheckIn,
		};
		onSave(payload);
		reset();
		onClose();
	};

	if (!vehicle) return null;

	const curEmp = employees.find((e) => e.id === vehicle.assignedOfficerID);
	const checkoutEmpID = watch("checkoutEmpID");
	const selEmp = employees.find((e) => e.id === +checkoutEmpID);
	const checkoutMileage = watch("checkoutMileage");
	const performInspection = watch("performInspection");

	const employeeOptions = employees
		.filter((e) => e.isActive)
		.map((e) => ({
			value: String(e.id),
			label: `${e.badge} — ${e.rank} ${e.lastName}, ${e.firstName}`,
		}));

	const mileageWarning =
		checkoutMileage &&
		vehicle.odometer &&
		+checkoutMileage < vehicle.odometer;

	const mileageUpdate =
		checkoutMileage && +checkoutMileage > (vehicle.odometer || 0);

	const isSaveDisabled = !isCheckIn && !checkoutEmpID;

	const dialogTitle = isCheckIn
		? `Check In — ${vehicle.unitNumber}`
		: `Check Out — ${vehicle.unitNumber}`;

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						pt: 1,
					}}
				>
					{/* Vehicle Summary */}
					<Box
						sx={{
							display: "flex",
							gap: 1.5,
							p: 1.5,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							alignItems: "center",
							bgcolor: "background.default",
						}}
					>
						<Typography sx={{ fontSize: 22 }}>🚗</Typography>
						<Box>
							<Typography variant="body2" fontWeight={800}>
								{vehicle.unitNumber} — {vehicle.year}{" "}
								{vehicle.make} {vehicle.model}
							</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mt: 0.25,
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									{vehicle.division || "—"}
								</Typography>
								{vehicle.status && (
									<Typography
										variant="caption"
										fontWeight={700}
										sx={{
											px: 1,
											borderRadius: 10,
											fontSize: 11,
											bgcolor:
												vehicle.status === "In Service"
													? "rgba(34,197,94,0.12)"
													: "rgba(239,68,68,0.1)",
											color:
												vehicle.status === "In Service"
													? "success.main"
													: "error.main",
										}}
									>
										{vehicle.status}
									</Typography>
								)}
							</Box>
						</Box>
					</Box>

					{/* Currently Assigned To */}
					<Box
						sx={{
							p: 1.5,
							bgcolor: curEmp
								? "rgba(234,179,8,0.08)"
								: "background.default",
							border: "1px solid",
							borderColor: curEmp
								? "rgba(234,179,8,0.3)"
								: "divider",
							borderRadius: 1,
						}}
					>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								mb: 0.5,
								display: "block",
							}}
						>
							Currently Assigned To
						</Typography>
						{curEmp ? (
							<Typography variant="body2" fontWeight={600}>
								{curEmp.rank} {curEmp.firstName}{" "}
								{curEmp.lastName}
								<Typography
									component="span"
									variant="caption"
									color="text.secondary"
									fontFamily="monospace"
									sx={{ ml: 1 }}
								>
									{curEmp.badge}
								</Typography>
							</Typography>
						) : (
							<Typography
								variant="caption"
								color="text.disabled"
								sx={{ fontStyle: "italic" }}
							>
								Unassigned
							</Typography>
						)}
					</Box>

					{/* Check Out To (checkout only) */}
					{!isCheckIn && (
						<Box>
							<DropdownController
								name="checkoutEmpID"
								label="Check Out To *"
								options={employeeOptions}
								control={control}
								errors={errors}
								placeholder="— Select employee —"
							/>
							{selEmp && (
								<Box
									sx={{
										mt: 1,
										p: 1,
										bgcolor: "rgba(99,102,241,0.06)",
										border: "1px solid rgba(99,102,241,0.2)",
										borderRadius: 1,
									}}
								>
									<Typography
										variant="caption"
										fontWeight={600}
									>
										{selEmp.rank} {selEmp.firstName}{" "}
										{selEmp.lastName}
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ ml: 0.75 }}
									>
										{selEmp.division || ""}
									</Typography>
								</Box>
							)}
						</Box>
					)}

					{/* Division (checkout only) */}
					{!isCheckIn && (
						<Box>
							<DropdownController
								name="checkoutDivision"
								label="Division"
								options={[
									{ value: "", label: "— None —" },
									...divisionOptions,
								]}
								control={control}
								errors={errors}
								placeholder="— Select division —"
							/>
						</Box>
					)}

					{/* Mileage */}
					<Box>
						<TextFieldController
							name="checkoutMileage"
							label="Odometer Reading (mi) — optional"
							control={control}
							errors={errors}
							type="number"
							placeholder={
								vehicle.odometer
									? vehicle.odometer.toLocaleString()
									: "Enter mileage..."
							}
						/>
						{mileageWarning && (
							<Typography
								variant="caption"
								color="error"
								fontWeight={700}
								sx={{ mt: 0.5, display: "block" }}
							>
								⚠ Entered mileage is less than current odometer
								({vehicle.odometer?.toLocaleString()} mi)
							</Typography>
						)}
						{mileageUpdate && (
							<Typography
								variant="caption"
								color="success.main"
								sx={{ mt: 0.5, display: "block" }}
							>
								✓ Vehicle odometer will be updated to{" "}
								{(+checkoutMileage).toLocaleString()} mi
							</Typography>
						)}
					</Box>

					{/* Check-in confirmation message */}
					{isCheckIn && (
						<Box
							sx={{
								p: 1.5,
								bgcolor: "rgba(34,197,94,0.06)",
								border: "1px solid rgba(34,197,94,0.2)",
								borderRadius: 1,
							}}
						>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								This will clear the current assignment and mark{" "}
								{vehicle.unitNumber} as unassigned.
							</Typography>
						</Box>
					)}

					{/* Notes */}
					<Box>
						<TextFieldController
							name="checkoutNotes"
							label="Notes (optional)"
							control={control}
							errors={errors}
							placeholder={
								isCheckIn
									? "Condition notes, reason for check-in..."
									: "Purpose, shift, notes..."
							}
						/>
					</Box>

					{/* Perform Inspection Toggle */}
					{canPerformInspection && (
						<Box
							sx={{
								p: 1.5,
								bgcolor: performInspection
									? "rgba(99,102,241,0.08)"
									: "background.default",
								border: "1px solid",
								borderColor: performInspection
									? "rgba(99,102,241,0.3)"
									: "divider",
								borderRadius: 1,
								cursor: "pointer",
								userSelect: "none",
							}}
							onClick={() =>
								setValue(
									"performInspection",
									!performInspection,
								)
							}
						>
							<FormControlLabel
								control={
									<Checkbox
										checked={performInspection || false}
										onChange={(e) =>
											setValue(
												"performInspection",
												e.target.checked,
											)
										}
										size="small"
									/>
								}
								label={
									<Box>
										<Typography
											variant="body2"
											fontWeight={700}
											sx={{ fontSize: 13 }}
										>
											Perform Inspection
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Launch inspection wizard after
											confirming{" "}
											{isCheckIn
												? "check-in"
												: "check-out"}
										</Typography>
									</Box>
								}
							/>
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					disabled={isSaveDisabled}
					tooltipText={
						isCheckIn ? "Confirm Check In" : "Confirm Check Out"
					}
				/>
				<PATCloseButton onClick={onClose} tooltipText="Cancel" />
			</DialogActions>
		</Dialog>
	);
};

export default CheckInOutVehicleDialog;
